"use client";

import { colors, util } from "@/common/theme";
import CustomSplitText from "@/mono/components/CustomSplitText";
import ShuffledText from "@/mono/components/ShuffledText";
import styled from "styled-components";
import { ScrollingText } from "../../ScrollingText";
import { SectionTitle } from "../../SectionTitle";

import { Project } from "@/components/Project";
import { mediaTabletDown as mediaMobile } from "@/mono/lib/responsive";
import { mediaTabletOnly } from "@/mono/lib/responsive";
import { setTypography } from "@/mono/lib/styles/mixins";
import { useGlobalStore } from "@/mono/store/global";
import { useCallback, useEffect, useRef } from "react";
import BorderedIcon from "../../BorderedIcon";
import useResize from "mono/lib/hooks/useResize";
import SplitText from "mono/components/SplitText";

export function HomeWhatWeveCreated({ projects }) {
  const projectsRef = useRef(null);
  const windowSize = useGlobalStore((s) => s.windowSize);

  // Calculate height of projects
  const measureProjectsHeight = useCallback(() => {
    if (!projectsRef.current) return;

    const children = projectsRef.current.children;
    let maxHeight = 0;

    for (const child of children) {
      // Use layout metrics to avoid fractional rounding issues
      const rect = child.getBoundingClientRect();
      const bottom = child.offsetTop + rect.height;
      if (bottom > maxHeight) maxHeight = bottom;
    }

    projectsRef.current.style.height = `${maxHeight}px`;
  }, []);

  // useResize(
  //   () => {
  //     measureProjectsHeight();
  //   },
  //   { immediate: true },
  //   [projects, windowSize]
  // );

  // Recalculate on mount and when children resize
  useEffect(() => {
    if (!projectsRef.current) return;

    // Run after mount and next paint
    requestAnimationFrame(() => {
      measureProjectsHeight();

      requestAnimationFrame(measureProjectsHeight);
    });

    // Observe child size changes
    const ro = new ResizeObserver(() => {
      measureProjectsHeight();
    });
    const children = Array.from(projectsRef.current.children);
    children.forEach((c) => ro.observe(c));

    return () => {
      ro.disconnect();
    };
  }, [projects, measureProjectsHeight]);

  // const alignMetaColumn = [1, 2];

  return (
    <>
      <SectionTitle>What we've done so far</SectionTitle>
      <Wrapper>
        <div className="wwc-top">
          <ScrollingText>Discover your impact</ScrollingText>
          <CustomSplitText>
            9CA maakt van jouw bedrijf een sterk merk. Met strategie, branding,
            websites, campagnes, AI, design en online marketing brengen we jouw
            verhaal tot leven én laten we je bedrijf groeien.
          </CustomSplitText>
        </div>

        <div className="wwc-projects-title-wrapper">
          <CustomSplitText className="wwc-projects" tag="h5">
            Projects
          </CustomSplitText>
          <div className="wwc-projects-title-small-text-wrapper">
            <ShuffledText
              animateOnScroll
              className="wwc-projects-title-small-text-1"
              useRandomText
            ></ShuffledText>
            <ShuffledText
              animateOnScroll
              className="wwc-projects-title-small-text-2"
              useRandomText
            ></ShuffledText>
            <ShuffledText
              animateOnScroll
              className="wwc-projects-title-small-text-3"
              useRandomText
            ></ShuffledText>
          </div>
        </div>

        <StyledProjects ref={projectsRef}>
          {projects.map((project, index) => {
            const coverImage = project.featuredImage || project.coverImage;

            return (
              <Project
                i={index}
                key={index}
                {...project}
                coverImage={coverImage}
              />
            );
          })}
        </StyledProjects>
        <MoreProjects>
          <div className="more__inner">
            <SplitText
              tag="h3"
              animation="charDoubleClipped"
              type="chars"
              className="more__title"
              animateOnScroll={true}
              dangerouslySetInnerHTML={{
                __html: "All our <strong>projects</strong>",
              }}
            ></SplitText>

            <BorderedIcon href="/projects" />
          </div>
        </MoreProjects>
      </Wrapper>
    </>
  );
}

const Wrapper = styled.section`
  position: relative;
  width: 100%;

  ${util.inner}

  // Wicky
  .project-0 {
    left: 50rem;
    top: 100rem;
    width: 1500rem;
    aspect-ratio: 16/9;
    .project-image,
    video {
      aspect-ratio: 16/9;
    }
  }

  // AB Inbev
  .project-1 {
    top: 400rem;
    right: 0rem;
    width: 600rem;
    .project-image {
      aspect-ratio: 5/7;
    }
  }

  // Fruit op je werk
  .project-2 {
    top: 1600rem;
    left: 0rem;

    width: 800rem;
    .project-image {
      aspect-ratio: 5/7;
    }
  }

  // Traksi
  .project-3 {
    top: 2150rem;
    right: calc(${(props) => props.theme.misc.basePadding()} + 50rem);

    width: 1200rem;
    .project-image {
      aspect-ratio: 1/1;
    }
  }

  // AK Advocaten
  .project-4 {
    top: 3950rem;
    left: 50%;
    transform: translateX(-50%);
    padding-bottom: 200rem;

    width: calc(100% - ${(props) => props.theme.misc.basePadding()} * 4);
    .project-image {
      aspect-ratio: 16/9;
    }
  }

  // Yuki
  .project-5 {
    top: 6000rem;
    left: 50rem;

    width: 1500rem;
    height: auto;
  }

  // Hotel Keyser
  .project-6 {
    top: 6200rem;
    right: 0;

    width: 600rem;
    height: auto;
  }

  // Badhuys
  .project-7 {
    top: 7600rem;
    left: 0;

    width: 800rem;
    height: auto;
  }

  // Yeaz
  .project-8 {
    top: 7800rem;
    right: 200rem;

    width: 1200rem;
    height: auto;
  }

  .txt-shuffle {
    color: ${({ theme }) => theme.theme.projectMeta};
  }

  .wwc-top {
    display: flex;
    position: relative;

    .scrolling-text {
      max-width: 1350rem;
    }

    p {
      left: 50%;
      position: absolute;
    }
  }

  .wwc-projects-title-wrapper {
    position: relative;
    padding: 200rem 300rem 200rem 0rem;

    h5 {
      text-align: right;
    }

    /* margin-top: 400rem; */

    .wwc-projects {
      /* position: absolute; */

      color: ${({ theme }) => theme.theme.floatingTitle};
      ${setTypography("floatingTitle")}
    }

    .wwc-projects-title-small-text-wrapper {
      display: flex;
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;

      .txt-shuffle {
        max-width: 300rem;
      }
    }

    .wwc-projects-title-small-text-1 {
      position: absolute;
      left: 10%;
      bottom: 40%;
    }

    .wwc-projects-title-small-text-2 {
      position: absolute;
      left: 45%;
      bottom: 40%;
    }

    .wwc-projects-title-small-text-3 {
      position: absolute;
      left: 80%;
      bottom: 40%;
    }
  }

  ${mediaTabletOnly(`
    /* Make project images a bit less tall on tablet while keeping full width */
    .project-img .project-image {
      aspect-ratio: 3/2;
    }
  `)}

  ${mediaMobile(`
    .wwc-top {
      flex-direction: column;
    }

    .scrolling-text {
      max-width: unset !important;
      display: block;
    }

    p {
      position: relative !important;
      left: 0 !important;
      display: block;
      width: 100%;
      margin: 50rem 0rem !important;
    }
  `)}
`;

// TODO: Calc?
const StyledProjects = styled.div`
  position: relative;

  ${mediaMobile(`
    height: auto !important;
    display: flex;
    flex-flow: column nowrap;
  `)}
`;

const MoreProjects = styled.div`
  position: relative;
  padding-top: 350rem;

  display: flex;
  justify-content: center;
  align-items: center;

  h3 {
    font-weight: 100 !important;
    strong {
      font-size: inherit;
    }
  }

  .more__inner {
    display: flex;

    flex-flow: column nowrap;
    gap: 50rem;
    justify-content: center;
    align-items: center;
    h3 {
      text-align: center;
    }

    // TEMP:
    /* * {
      color: ${colors.grey} !important;
      fill: ${colors.grey} !important;
      stroke: ${colors.grey} !important;
      border-color: ${colors.grey} !important;
    } */
  }

  ${mediaMobile(`
    padding-top: 100rem;
    padding-bottom: 100rem;
  `)}
`;
